/**
 * Installer Activation API Client
 *
 * Token-scoped installer activation pipeline used by the wizard mounted at
 *   /activate/:chargerCode
 *
 * The raw activation token comes from the QR-encoded `?t=` query param.
 * It is sent on every request as a query parameter — never logged, never
 * included in response bodies. The backend stores only a hashed form and
 * consumes the token atomically on finalize, so a finalized identity's
 * token cannot be reused.
 *
 * Backend mount: /api/installer/activation/:chargerCode/...
 */

import axios from 'axios';

const BASE = `${(import.meta.env.VITE_API_URL || '').replace(/\/+$/, '')}/installer/activation`;

function activationUrl(chargerCode, token, subpath = '') {
  const code = encodeURIComponent(chargerCode);
  const t = encodeURIComponent(token);
  return `${BASE}/${code}${subpath}?t=${t}`;
}

/**
 * Step 1 — start. Validates the token, returns the identity row and the
 * public charging URL. Idempotent: re-runnable to re-seed state on reload.
 */
export async function startActivation(chargerCode, token) {
  const res = await axios.get(activationUrl(chargerCode, token));
  return res.data;
}

/**
 * Step 2 — verify OCPP. Returns either:
 *   { linked: true, station: {...} }            — already linked
 *   { linked: false, candidates: [...] }        — unlinked, list options
 * Read-only.
 */
export async function verifyOcppForActivation(chargerCode, token) {
  const res = await axios.post(activationUrl(chargerCode, token, '/verify-ocpp'));
  return res.data;
}

/**
 * Step 3 — link the GCEV identity to a specific OCPP station_id (and
 * optionally a manufacturer serial). Idempotent if the same pair is
 * resubmitted.
 */
export async function linkChargerForActivation(chargerCode, token, body) {
  const res = await axios.post(activationUrl(chargerCode, token, '/link'), body);
  return res.data;
}

/**
 * Step 4 — upsert verified_stations row + assign location and pricing.
 */
export async function configureChargerForActivation(chargerCode, token, body) {
  const res = await axios.post(activationUrl(chargerCode, token, '/configure'), body);
  return res.data;
}

/**
 * Step 5 — atomic transition `ocpp_seen` → `activated`. Consumes the
 * activation token; subsequent requests with this token will be rejected.
 * Idempotent: a second finalize call on an already-activated identity
 * returns `{ alreadyActivated: true, ... }` rather than erroring.
 */
export async function finalizeActivation(chargerCode, token) {
  const res = await axios.post(activationUrl(chargerCode, token, '/finalize'));
  return res.data;
}
